import { cn } from '@duck/ui'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useRef, useEffect } from 'react'
import { X } from 'lucide-react'
import { TranslationKey, useTar } from '@/modules/i18n'

// Shared tab styles - exported for use by AddTabButton
export const getTabClassName = (isActive: boolean, isDragging: boolean, isThin: boolean, className?: string) =>
    cn(
        'relative h-8 cursor-pointer select-none',
        isThin ? 'ps-3 pe-2' : 'px-5',
        'flex items-center justify-center',
        'before:border-[1px] before:border-border before:border-solid before:border-muted/60 before:border-b-0',
        'text-sm font-medium transition-colors outline-none',
        'before:absolute before:inset-0 before:rounded-t-md',
        'before:[transform:perspective(8px)_rotateX(2deg)]',
        'before:origin-bottom before:transition-colors',
        isActive
            ? 'text-foreground before:bg-primary/75'
            : 'text-muted-foreground hover:text-foreground before:bg-card',
        isDragging && 'cursor-grabbing',
        className
    )

export interface WorkspaceTabProps {
    sortableId: string
    label: TranslationKey
    index: number
    isActive: boolean
    isDragActive: boolean
    className?: string
    onActivateTab: () => void
    onRemoveTab?: (method?: 'button' | 'middle_click') => void
    tabRef?: (el: HTMLDivElement | null) => void
    disabled?: boolean
}

export const WorkspaceTab = ({
    sortableId,
    label,
    index,
    isActive,
    isDragActive,
    className,
    onActivateTab,
    onRemoveTab,
    tabRef,
    disabled,
}: WorkspaceTabProps) => {
    const wasDragActiveRef = useRef(false)
    const { t } = useTar()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        isDragging,
    } = useSortable({
        id: sortableId,
        disabled,
    })

    const justDropped = wasDragActiveRef.current && !isDragActive

    useEffect(() => {
        wasDragActiveRef.current = isDragActive
    }, [isDragActive])

    const style = {
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: justDropped ? 'none' : undefined,
        zIndex: isDragging ? 200 : 100 - index + (isActive ? 100 : 0),
        opacity: isDragging ? 0 : 1,
    }

    return (
        <div
            ref={(el) => {
                setNodeRef(el)
                tabRef?.(el)
            }}
            style={style}
            {...attributes}
            {...listeners}
            role="tab"
            tabIndex={0}
            aria-label={t(label)}
            aria-selected={isActive}
            aria-current={isActive ? 'page' : undefined}
            className={cn('group', getTabClassName(isActive, isDragging, false, className), onRemoveTab && 'pe-2')}
            onClick={onActivateTab}
            onAuxClick={(e) => {
                if (e.button === 1 && onRemoveTab) {
                    e.preventDefault()
                    onRemoveTab('middle_click')
                }
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onActivateTab()
                }
            }}
        >
            <span className="relative z-10 pt-1 whitespace-nowrap">
                {t(label)}
            </span>
            {onRemoveTab && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('common.close')}
                    className="relative z-10 mx-1 mt-1 p-0.5 rounded-xl opacity-0 group-hover:opacity-100 hover:bg-muted/60 transition-all"
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemoveTab?.('button')
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                            e.preventDefault()
                            onRemoveTab?.('button')
                        }
                    }}
                >
                    <X className="size-3" />
                </span>
            )}
        </div>
    )
}

// Static overlay component for DragOverlay - not affected by layout shifts
export interface WorkspaceTabOverlayProps {
    label: TranslationKey
    isActive: boolean
    className?: string
}

export const WorkspaceTabOverlay = ({
    label,
    isActive,
    className,
}: WorkspaceTabOverlayProps) => {
    const { t } = useTar()
    return (
        <div
            className={getTabClassName(isActive, true, false, className)}
            style={{ zIndex: 300 }}
        >
            <span className="relative z-10 pt-1 whitespace-nowrap">{t(label)}</span>
        </div>
    )
}
